import { useEffect, useRef, useState, useTransition } from 'react';

import { useVoiceoverContext } from '../_context/VoiceoverContext';
import {
    CloneMyVoiceState,
    TAB_TO_EVENT_TYPE,
    Tabs,
    TextToSpeechState,
    useVoiceoverTabContext,
} from '../_context/VoiceoverTabContext';

const UNAVAILABLE_ERROR = 'Voiceover service is temporarily unavailable. Please try again later.';

export interface UseGenerateAudioParams {
    generateRequestBody: () => BodyInit;
    useStreaming?: boolean;
    onSuccess?: () => void;
    onClose: () => void;
}

export interface UseGenerateAudioResult {
    generate: () => void;
    close: () => void;
    isPending: boolean;
    errorMessage: string | null;
}

export function useGenerateAudio({
    generateRequestBody,
    useStreaming = false,
    onSuccess,
    onClose,
}: UseGenerateAudioParams): UseGenerateAudioResult {
    const [isPending, startTransition] = useTransition();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const {
        CreditsApi: { refreshCredits },
    } = useVoiceoverContext();
    const { activeTabState, updateState, activeTab } = useVoiceoverTabContext();
    const { aiContentId, lastAiContentId } = activeTabState;

    const handleClose = (): void => {
        setErrorMessage(null);
        onClose();
    };

    // Track created object URLs so we can revoke them on unmount to avoid memory leaks
    const streamUrlRef = useRef<string | null>(null);
    const finalUrlRef = useRef<string | null>(null);

    useEffect(() => {
        return () => {
            if (streamUrlRef.current) URL.revokeObjectURL(streamUrlRef.current);
            if (finalUrlRef.current) URL.revokeObjectURL(finalUrlRef.current);
        };
    }, []);

    const generate = (): void => {
        setErrorMessage(null);

        const voiceId =
            (activeTabState as TextToSpeechState).selectedVoice ||
            (activeTabState as CloneMyVoiceState).generatedVoiceId;

        const eventType = TAB_TO_EVENT_TYPE[activeTab];

        startTransition(async () => {
            try {
                const requestUrl = {
                    [Tabs.TextToSpeech]: '/api/voiceover/text-to-speech',
                    [Tabs.CloneMyVoice]: '/api/voiceover/text-to-speech',
                    [Tabs.VoiceChanger]: '/api/voiceover/speech-to-speech',
                }[activeTab];

                const body = generateRequestBody();

                const headers: HeadersInit = {
                    'X-AI-Content-Voiceover-Type': eventType,
                };

                if (typeof body === 'string') headers['Content-Type'] = 'application/json';

                // information we need to send from client to server for event emissions
                if (aiContentId) headers['X-AI-Content-Id'] = aiContentId;
                if (lastAiContentId) headers['X-Last-AI-Content-Id'] = lastAiContentId;

                const res = await fetch(requestUrl, {
                    method: 'POST',
                    headers,
                    body,
                });

                if (!res.ok) {
                    throw new Error(`HTTP Error: status ${res.status}`);
                }

                // server sent back the new content id as a response header
                const newAiContentId = res.headers.get('X-AI-Content-Id');
                const contentIdUpdate = newAiContentId
                    ? { lastAiContentId: aiContentId, aiContentId: newAiContentId }
                    : {};

                const supportsStreaming =
                    useStreaming &&
                    res.body !== null &&
                    typeof MediaSource !== 'undefined' &&
                    MediaSource.isTypeSupported('audio/mpeg');

                if (!supportsStreaming) {
                    // Close modal immediately; leave generatedAudioUrl unchanged so the [audioUrl]
                    // effect in GeneratedVoicePlayer interrupts playback when the blob URL arrives
                    updateState({
                        generatedAudioVoiceId: voiceId,
                        generatedAudioIsComplete: false,
                        generatedAudioIsFetching: true,
                        generatedAudioHasPlayed: false,
                        generatedAudioHasDownloaded: false,
                        ...contentIdUpdate,
                    });

                    onSuccess?.();
                    handleClose();

                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    finalUrlRef.current = url;
                    updateState({
                        generatedAudioUrl: url,
                        generatedAudioIsComplete: true,
                        generatedAudioIsFetching: false,
                    });
                    return;
                }

                const mediaSource = new MediaSource();
                const streamUrl = URL.createObjectURL(mediaSource);
                streamUrlRef.current = streamUrl;
                // Collect all chunks so we can build a regular Blob URL when streaming
                // completes. MediaSource blob URLs cannot be downloaded or replayed after
                // endOfStream(), so we swap to a proper Blob URL once we have all the data
                const chunks: Uint8Array[] = [];

                updateState({
                    generatedAudioUrl: streamUrl,
                    generatedAudioVoiceId: voiceId,
                    generatedAudioIsComplete: false,
                    generatedAudioHasPlayed: false,
                    generatedAudioHasDownloaded: false,
                    ...contentIdUpdate,
                });

                onSuccess?.();
                handleClose();

                mediaSource.addEventListener('sourceopen', () => {
                    const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                    const reader = res.body!.getReader();

                    const pump = async (): Promise<void> => {
                        const { done, value } = await reader.read();
                        if (done) {
                            // endOfStream() throws if the SourceBuffer is still processing a
                            // previous appendBuffer() call (updating === true). Defer until idle
                            if (!sourceBuffer.updating) {
                                mediaSource.endOfStream();
                            } else {
                                sourceBuffer.addEventListener('updateend', () => mediaSource.endOfStream(), {
                                    once: true,
                                });
                            }
                            // MediaSource blob URLs can't be downloaded or replayed after endOfStream(), so swap
                            // to a standard Blob URL built from the collected chunks once streaming is complete
                            const finalUrl = URL.createObjectURL(
                                new Blob(chunks as BlobPart[], { type: 'audio/mpeg' }),
                            );
                            finalUrlRef.current = finalUrl;
                            // Revoke the MediaSource URL now that we have the final Blob URL
                            if (streamUrlRef.current) {
                                URL.revokeObjectURL(streamUrlRef.current);
                                streamUrlRef.current = null;
                            }
                            updateState({
                                generatedAudioUrl: finalUrl,
                                generatedAudioIsComplete: true,
                            });
                            void refreshCredits();
                            return;
                        }
                        // appendBuffer() also throws if called while the SourceBuffer is still processing the
                        // previous chunk (updating === true). Wait until idle before appending the next chunk
                        if (sourceBuffer.updating) {
                            await new Promise<void>((resolve) => {
                                sourceBuffer.addEventListener('updateend', () => resolve(), { once: true });
                            });
                        }
                        chunks.push(value);
                        sourceBuffer.appendBuffer(value);
                        // Recurse only after the append completes to keep chunks in order
                        sourceBuffer.addEventListener('updateend', () => void pump(), { once: true });
                    };

                    void pump().catch(() => {
                        setErrorMessage(UNAVAILABLE_ERROR);
                        updateState({ generatedAudioIsComplete: true });
                        void refreshCredits();
                    });
                });
            } catch {
                setErrorMessage(UNAVAILABLE_ERROR);
            } finally {
                // Refresh credits on every exit from the try block. The streaming pump's async completion/error paths
                // call refreshCredits() independently since they fire after this finally block has already settled
                void refreshCredits();
            }
        });
    };

    return { generate, close: handleClose, isPending, errorMessage };
}
